import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Lightbulb,
  Search,
  Eye,
  Loader2,
  Filter,
  TrendingUp,
  FileText,
  Brain,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import type { User } from '../App';
import type { ReviewerAssignment } from '../api/innovations';
import { getMyAssignments, acceptAssignment, declineAssignment, updateFilingRequestStatus } from '../api/innovations';
import { getReviewerProfile } from '../api/reviewers';
import { Alert, AlertDescription } from './ui/alert';

const STAGES = ['idea', 'exploration', 'concept', 'prototype', 'demo'] as const;

interface ReviewerDashboardProps {
  user: User;
  onReviewClick: (id: string, options?: { assessmentMode?: 'ai' | 'manual' }) => void;
  onNavigateToCompleteProfile?: () => void;
  onNavigateToProfileEdit?: () => void;
}

export function ReviewerDashboard({ user, onReviewClick, onNavigateToCompleteProfile, onNavigateToProfileEdit }: ReviewerDashboardProps) {
  const [assignments, setAssignments] = useState<ReviewerAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileIncomplete, setProfileIncomplete] = useState<boolean | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectionFilter, setSectionFilter] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('all');
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState<string | null>(null);
  const [showAssessmentMethod, setShowAssessmentMethod] = useState(false);
  const [selectedInnovationForAssessment, setSelectedInnovationForAssessment] = useState<string | null>(null);
  const [filingStatusLoadingId, setFilingStatusLoadingId] = useState<string | null>(null);

  const loadAssignments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyAssignments({});
      setAssignments(res.assignments || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  useEffect(() => {
    if (!onNavigateToCompleteProfile) return;
    getReviewerProfile()
      .then((r) => setProfileIncomplete(!r.profile?.profile_completed))
      .catch(() => setProfileIncomplete(null));
  }, [onNavigateToCompleteProfile]);

  const pending = useMemo(() => assignments.filter((a) => a.assignment_details.assignment_status === 'pending'), [assignments]);
  const inProgress = useMemo(() => assignments.filter((a) => a.assignment_details.assignment_status === 'in_progress' || a.assignment_details.assignment_status === 'accepted'), [assignments]);
  const completed = useMemo(() => assignments.filter((a) => a.assignment_details.assignment_status === 'completed'), [assignments]);

  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (sectionFilter === 'pending') list = pending;
    else if (sectionFilter === 'in_progress') list = inProgress;
    else if (sectionFilter === 'completed') list = completed;
    return list.filter((a) => {
      const matchesSearch =
        !searchQuery ||
        a.submission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.submission.keywords && a.submission.keywords.some((k: string) => k.toLowerCase().includes(searchQuery.toLowerCase())));
      return matchesSearch;
    });
  }, [assignments, sectionFilter, pending, inProgress, completed, searchQuery]);

  const handleAccept = async (assignmentUuid: string) => {
    setAcceptingId(assignmentUuid);
    try {
      await acceptAssignment(assignmentUuid);
      await loadAssignments();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to accept assignment');
    } finally {
      setAcceptingId(null);
    }
  };

  const handleDeclineSubmit = async (assignmentUuid: string) => {
    setDecliningId(assignmentUuid);
    try {
      await declineAssignment(assignmentUuid, declineReason || undefined);
      setShowDeclineModal(null);
      setDeclineReason('');
      await loadAssignments();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to decline assignment');
    } finally {
      setDecliningId(null);
    }
  };

  const handleInitiateAssessment = (submissionUuid: string) => {
    setSelectedInnovationForAssessment(submissionUuid);
    setShowAssessmentMethod(true);
  };

  const handleSelectAssessmentMethod = (method: 'ai' | 'manual') => {
    if (selectedInnovationForAssessment) {
      onReviewClick(selectedInnovationForAssessment, { assessmentMode: method });
      setShowAssessmentMethod(false);
      setSelectedInnovationForAssessment(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-amber-600 border-amber-600">Pending</Badge>;
      case 'accepted':
      case 'in_progress':
        return <Badge variant="outline" className="text-blue-600 border-blue-600">In progress</Badge>;
      case 'completed':
        return <Badge className="bg-[var(--royal)] text-white">Completed</Badge>;
      case 'declined':
        return <Badge variant="secondary">Declined</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStageBadge = (stage: string) => (
    <Badge variant="outline" className="capitalize">
      {String(stage).replace('_', ' ')}
    </Badge>
  );

  const getUpdateTag = (a: ReviewerAssignment) => {
    if (a.submission.filing_transition_requested_status) {
      return (
        <div className="space-y-1">
          <Badge className="bg-amber-600 text-white">
            Transition Pending Superadmin: {a.submission.filing_transition_requested_status.replace(/_/g, ' ')}
          </Badge>
          {a.submission.filing_transition_rejection_reason && (
            <p className="text-xs text-rose-700">Rejected reason: {a.submission.filing_transition_rejection_reason}</p>
          )}
        </div>
      );
    }
    if (a.submission.filing_request_status === 'ready_to_file') {
      return <Badge className="bg-emerald-600 text-white">Ready to File</Badge>;
    }
    if (a.submission.filing_request_status === 'in_support') {
      return <Badge className="bg-indigo-600 text-white">Filing Support In Progress</Badge>;
    }
    if (a.submission.filing_request_status === 'closed') {
      return <Badge variant="outline" className="text-slate-700 border-slate-400">Filing Request Closed</Badge>;
    }
    if (a.submission.filing_request_notified_to_reviewer) {
      const types = (a.submission.filing_request_selected_types || []).join(', ');
      return (
        <div className="space-y-1">
          <Badge className="bg-emerald-600 text-white">Innovator Requested Filing Guidance</Badge>
          {types && <p className="text-xs text-slate-600">Selected: {types}</p>}
        </div>
      );
    }
    if (a.submission.feedback_approval_status === 'changes_requested') {
      return <Badge variant="outline" className="text-amber-700 border-amber-400">Superadmin Requested Updates</Badge>;
    }
    if (a.submission.feedback_approval_status === 'pending_superadmin_review') {
      return <Badge variant="outline" className="text-blue-700 border-blue-400">Awaiting Superadmin Approval</Badge>;
    }
    return <Badge variant="secondary">No New Updates</Badge>;
  };

  const handleFilingStatusUpdate = async (submissionUuid: string, status: 'in_support' | 'ready_to_file' | 'closed') => {
    setFilingStatusLoadingId(`${submissionUuid}:${status}`);
    try {
      await updateFilingRequestStatus(submissionUuid, status);
      alert('Status transition request sent to superadmin for approval.');
      await loadAssignments();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to update filing status');
    } finally {
      setFilingStatusLoadingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      {profileIncomplete === true && onNavigateToCompleteProfile && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>Complete your profile to receive assignments from administrators.</span>
            <Button size="sm" onClick={onNavigateToCompleteProfile}>
              Complete profile
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <h1 className="text-[var(--navy)] font-display font-semibold mb-2">Independent Reviewer Dashboard</h1>
        <p className="text-[var(--navy)]/70">
          Your assigned innovations only. Accept or decline new assignments, then complete assessments.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Total Assigned</CardTitle>
            <Lightbulb className="w-5 h-5 text-[var(--gold)]" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{assignments.length}</div>
            <p className="text-[var(--navy)]/60">My assignments</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Pending</CardTitle>
            <Clock className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{pending.length}</div>
            <p className="text-[var(--navy)]/60">Accept or decline</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">In progress</CardTitle>
            <Filter className="w-5 h-5 text-[var(--gold)]" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{inProgress.length}</div>
            <p className="text-[var(--navy)]/60">Awaiting evaluation</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Completed</CardTitle>
            <TrendingUp className="w-5 h-6 text-[var(--royal)]" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{completed.length}</div>
            <p className="text-[var(--navy)]/60">Assessments finished</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-[var(--navy)] font-display">My Assignments</CardTitle>
            <CardDescription className="text-[var(--navy)]/70">
              Innovations assigned to you. You only see and work on these.
            </CardDescription>
          </div>
          {onNavigateToProfileEdit && (
            <Button variant="outline" size="sm" onClick={onNavigateToProfileEdit}>
              Edit profile
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by title or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={sectionFilter} onValueChange={(v: typeof sectionFilter) => setSectionFilter(v)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending (accept/decline)</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Innovation</TableHead>
                <TableHead>Stage</TableHead>
                  <TableHead>Assignment status</TableHead>
                  <TableHead>Due</TableHead>
                <TableHead>Updates</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
                {filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[var(--navy)]/70 py-8">
                      {assignments.length === 0
                        ? 'No assignments yet. An administrator will assign innovations to you.'
                        : 'No submissions match your filters.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map((a) => {
                    const status = a.assignment_details.assignment_status;
                    const isPending = status === 'pending';
                    const isActive = status === 'in_progress' || status === 'accepted';
                    return (
                      <TableRow key={a.assignment_uuid}>
                    <TableCell>
                          <div>
                            <div className="text-slate-900">{a.submission.title}</div>
                            <div className="text-slate-500 text-sm">{a.submission.technology_area || '—'}</div>
                      </div>
                    </TableCell>
                        <TableCell>{getStageBadge(a.submission.current_state)}</TableCell>
                        <TableCell>{getStatusBadge(status)}</TableCell>
                    <TableCell>
                          {a.assignment_details.due_date ? (
                            <span className={a.assignment_details.days_remaining != null && a.assignment_details.days_remaining < 0 ? 'text-red-600' : ''}>
                              {a.assignment_details.due_date}
                              {a.assignment_details.days_remaining != null && ` (${a.assignment_details.days_remaining}d)`}
                            </span>
                          ) : (
                            '—'
                          )}
                    </TableCell>
                        <TableCell>{getUpdateTag(a)}</TableCell>
                    <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => onReviewClick(a.submission.submission_uuid)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                            {isPending && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleAccept(a.assignment_uuid)}
                                  disabled={acceptingId !== null}
                                >
                                  {acceptingId === a.assignment_uuid ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                  Accept
                                </Button>
                          <Button
                                  variant="outline"
                            size="sm"
                                  onClick={() => setShowDeclineModal(a.assignment_uuid)}
                                  disabled={decliningId !== null}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Decline
                                </Button>
                              </>
                            )}
                            {isActive && (
                              <Button size="sm" onClick={() => handleInitiateAssessment(a.submission.submission_uuid)}>
                                <FileText className="w-4 h-4 mr-1" />
                                Continue assessment
                          </Button>
                        )}
                            {a.submission.filing_request_notified_to_reviewer && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={filingStatusLoadingId !== null || !!a.submission.filing_transition_requested_status}
                                  onClick={() => handleFilingStatusUpdate(a.submission.submission_uuid, 'in_support')}
                                >
                                  Request In Support
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={filingStatusLoadingId !== null || !!a.submission.filing_transition_requested_status}
                                  onClick={() => handleFilingStatusUpdate(a.submission.submission_uuid, 'ready_to_file')}
                                >
                                  Request Ready to File
                                </Button>
                              </>
                            )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
                  })
                )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {showDeclineModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => {
            setShowDeclineModal(null);
            setDeclineReason('');
          }}
        >
          <Card
            className="max-w-md w-full border-slate-200 bg-white text-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
          <CardHeader>
              <CardTitle className="text-slate-900">Decline assignment</CardTitle>
              <CardDescription className="text-slate-600">Optionally provide a reason (visible to the administrator).</CardDescription>
          </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="e.g. Workload too high"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                maxLength={500}
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowDeclineModal(null); setDeclineReason(''); }}>
                  Cancel
                </Button>
                        <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDeclineSubmit(showDeclineModal)}
                  disabled={decliningId !== null}
                >
                  {decliningId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decline'}
                        </Button>
              </div>
          </CardContent>
        </Card>
        </div>
      )}

      {showAssessmentMethod && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assessment-method-title"
          onClick={() => {
            setShowAssessmentMethod(false);
            setSelectedInnovationForAssessment(null);
          }}
        >
          <Card
            className="max-w-2xl w-full border-slate-200 bg-white text-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <CardTitle id="assessment-method-title" className="text-slate-900">
                Select assessment method
              </CardTitle>
              <CardDescription className="text-slate-600">
                Choose how you would like to assess this innovation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleSelectAssessmentMethod('ai')}
                  className="group border-2 border-[rgba(201,168,76,0.2)] rounded-lg p-6 hover:border-[var(--gold)] hover:bg-[var(--gold-pale)] transition-all text-left"
                >
                  <div className="w-12 h-12 bg-[var(--gold-pale)] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[rgba(201,168,76,0.2)] transition-colors">
                    <Brain className="w-6 h-6 text-[var(--gold)]" />
                  </div>
                  <h3 className="text-[var(--navy)] mb-2 font-display">AI-assisted assessment</h3>
                  <p className="text-[var(--navy)]/70 text-sm">
                    Use AI-powered analysis to evaluate IP potential.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectAssessmentMethod('manual')}
                  className="group border-2 border-[rgba(201,168,76,0.2)] rounded-lg p-6 hover:border-[var(--royal)] hover:bg-[rgba(26,86,219,0.08)] transition-all text-left"
                >
                  <div className="w-12 h-12 bg-[rgba(26,86,219,0.15)] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[rgba(26,86,219,0.25)] transition-colors">
                    <FileText className="w-6 h-6 text-[var(--royal)]" />
                  </div>
                  <h3 className="text-[var(--navy)] mb-2 font-display">Manual assessment</h3>
                  <p className="text-[var(--navy)]/70 text-sm">
                    Detailed manual review with checklists and IP guidance.
                  </p>
                </button>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowAssessmentMethod(false);
                    setSelectedInnovationForAssessment(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
